// TaskPreview.jsx - Lightweight preview component for Gantt tooltips
import React from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { getStateConfig, getPriorityColor, isTaskOverdue } from '../../../../features/tasks/utils/taskStateUtils';

/**
 * Lightweight task preview for tooltips - optimized for performance
 * Renders only essential task information without heavy components
 */
const TaskPreview = ({ task }) => {
  const theme = useTheme();
  const { t } = useTranslation(['tasks', 'common']);

  const isOverdue = isTaskOverdue(task);
  const priorityColor = getPriorityColor(task.priority);
  const stateConfig = getStateConfig(task.state);
  const StateIcon = stateConfig.icon;

  return (
    <Box sx={{ p: 1.5, minWidth: 280, maxWidth: 400 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <AssignmentIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" fontWeight="600" noWrap>
          {task.title}
        </Typography>
      </Box>

      {/* Status chips */}
      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" mb={1.5}>
        <Chip
          icon={<StateIcon />}
          label={stateConfig.label}
          size="small"
          color={stateConfig.color}
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 20 }}
        />

        <Chip
          label={t(`tasks:priority.${task.priority.toLowerCase()}`)}
          size="small"
          color={priorityColor}
          sx={{ fontSize: '0.7rem', height: 20 }}
        />

        {isOverdue && (
          <Chip
            icon={<WarningIcon sx={{ fontSize: '0.8rem' }} />}
            label={t('tasks:task_card.overdue')}
            size="small"
            color="error"
            variant="filled"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        )}

        {task.state === 'SCHEDULED' && (
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: '0.8rem' }} />}
            label={t('tasks:task_card.awaiting_start')}
            size="small"
            color="info"
            variant="filled"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        )}
      </Box>

      {/* Description */}
      {task.description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            fontSize: '0.8rem',
          }}
        >
          {task.description}
        </Typography>
      )}

      {/* Dates */}
      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
        {task.scheduled_date && (
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" fontWeight="500">
              {t('tasks:task_card.scheduled_label')}:
            </Typography>
            <Typography variant="caption">
              {new Date(task.scheduled_date).toLocaleDateString()}
            </Typography>
          </Box>
        )}

        {task.due_date && (
          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" fontWeight="500">
              {t('tasks:task_card.due_label')}:
            </Typography>
            <Typography variant="caption" color={isOverdue ? 'error.main' : 'text.secondary'}>
              {new Date(task.due_date).toLocaleDateString()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Category */}
      {task.category?.name && (
        <Box mt={1.5}>
          <Chip
            label={task.category.name}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Box>
      )}

      {/* Assignees preview */}
      {task.assignees && task.assignees.length > 0 && (
        <Box mt={1} display="flex" alignItems="center" gap={0.5}>
          <Typography variant="caption" color="text.secondary">
            {t('common:labels.assignees')}:
          </Typography>
          <Typography variant="caption" fontWeight="500">
            {task.assignees.map(a => a.name || a.email).join(', ')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(TaskPreview);
